import React from "react";
import { Box, Tab } from "@mui/material";

import { StyledMainBox, StyledTabs } from "../../components/commonComponents/StyledComponents/styled";

import { fetchUsers, checkUserDetailsFromCookie, fetchRoles, checkUserPermissions, manageUsersPaginationCookie } from "../../api_calls/userManagement";
import { fetchCompanies } from "../../api_calls/companyRequests";

import AccessControl from "../../components/commonComponents/Hoc/AccessControl";

import PropTypes from "prop-types";
import UserList from "./tabData/UserList";
import { deletePaginationCookies, scrollTableListings } from "../../api_calls/utils";
import BrandUserlist from "./tabData/BrandUserList";
import { setBatchLoadingStatus } from "../../api_calls/requestConfig";

// to load tab content
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box mt={5}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Users = () => {
  const [value, setValue] = React.useState(0);
  const checkUserDetails = checkUserDetailsFromCookie();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  // deactivated sections for the logged in user
  let deactivatedSections = checkUserDetails.success === true ? checkUserDetails.user_detail.deactivated_sections : [];
  let showBrandsSection = checkUserDetails.user_detail.user_type === "cyreen" || !deactivatedSections.includes("campaign_list");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let tabsConfig = [];
  const showTabsView = (
    checkUserDetails.user_detail.user_type === "cyreen" &&
    permissionScope.user_list_can_edit_cyreen === true &&
    permissionScope.user_list_can_delete_cyreen === true
  )
    ||
    (
      permissionScope.user_list_can_edit_brand === true &&
      permissionScope.user_list_can_delete_brand === true
    );
  // tabs will only be visible to cyreen users
  // user_list_can_edit_cyreen
  // user_list_can_delete_cyreen
  if (showTabsView === true) {
    tabsConfig = [
      {
        label: "Users",
        icon: "",
        unique_key: "CustomersList",
        dashboard: <UserList usedFor="customers" />,
        filter_component: <></>,
      }
    ];

    // brand user tab
    if (permissionScope.user_list_can_edit_brand === true &&
      permissionScope.user_list_can_delete_brand === true && showBrandsSection === true) {
      tabsConfig.push(
        {
          label: "Brands",
          icon: "",
          unique_key: "brandUsersList",
          dashboard: <BrandUserlist usedFor="brands" />,
          filter_component: <></>,
        }
      );
    }

    // cyreen user tab
    if (checkUserDetails.user_detail.user_type === "cyreen" &&
      permissionScope.user_list_can_edit_cyreen === true &&
      permissionScope.user_list_can_delete_cyreen === true) {
      tabsConfig.push(
        {
          label: "Cyreen",
          icon: "",
          unique_key: "cyreenUsersList",
          dashboard: <UserList usedFor="cyreen" />,
          filter_component: <></>,
        }
      );
    }
  }

  const loadAllInfo = async () => {
    //needed to validate if edit/delete actions are allowed. If the assigned user role is not in the list of allowed roles, then the actions will be disabled --- fetchRoles()
    setBatchLoadingStatus(true)
    await Promise.all([
      fetchUsers(),
      fetchCompanies("users"),
      fetchRoles(false),
    ]).then(()=>setBatchLoadingStatus(false));
    // scroll to the last position where user was before refreshing the page
    scrollTableListings("users_pagination");
  }

  React.useEffect(() => {
    // check pagination cookie and set it in redux state
    if (typeof document.cookie.split("; ").find((row) => row.startsWith("users_pagination")) !== "undefined") {
      const paginationObj = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("users_pagination"))
          .split("=")[1]
      );
      manageUsersPaginationCookie(paginationObj);
    }
    // this will load the user list, roles and companies
    loadAllInfo();
    // delete pagination cookies for other pages.
    deletePaginationCookies("users");

    // handle scroll event to save the scroll position in cookie
    // this will be used to scroll the page to the last position where user was before refreshing the page
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      manageUsersPaginationCookie({ scroll_pos: currentScrollPos });
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };

  }, []);

  return (
    <>
      {/* ========== main component body =========== */}
      <StyledMainBox component="main">
        <Box height={30} />
        <h1>User List</h1>
        {/* ============ user tabs =========== */}
        {showTabsView === true ? (
          <>
            <Box
              sx={{
                display: "flex", // Make the Box a flex container
                position: "sticky",
                top: "0",
                zIndex: 1,
                backgroundColor: "#fff",
              }}
            >
              <Box component={"div"}>
                <StyledTabs value={value} onChange={handleChange} aria-label="data-exchange" textColor={"#F04B55"}>
                  {tabsConfig.map((tabDetail) => {
                    return (
                      <Tab key={tabDetail.unique_key} icon={tabDetail.icon} label={tabDetail.label} {...a11yProps(0)} />
                    );
                  })}
                </StyledTabs>
              </Box>
            </Box>
            {/* ============== tab contents =========== */}
            {tabsConfig.map((tabDetail, index) => {
              return (
                <CustomTabPanel value={value} index={index}>
                  {tabDetail.dashboard}
                </CustomTabPanel>
              );
            })}
          </>
        ) : (
          <UserList usedFor={"customer"} />
        )}
      </StyledMainBox>
    </>
  );
};

export default AccessControl(Users, ["user_list_can_view"], [], ["user_section", "user_list"]);
